import React from 'react';
import styled from 'styled-components';
import { RouteComponentProps } from 'react-router';
import useFetch from 'use-http';
import dayjs from 'dayjs';
import Empty from 'antd/es/empty';
import Divider from 'antd/es/divider';
import { textStyles } from '@allenai/varnish';

import { Graphviz } from '../../components/Graphviz';
import { ApiBase } from '../../api';

import aristoSrc from './logo-aristo-color-56px.svg';
import priorSrc from './logo-prior-color-56px.svg';
import universityOfWashingtonSrc from './logo-university-of-washington.svg';
import mosaicSrc from './logo-mosaic-color-56px.svg';
import semanticScholarSrc from './logo-semantic-scholar.svg';
import allenNlpSrc from './logo-allen-nlp-stacked.svg';
import israelSrc from './logo-flag-of-Israel-color-56px.svg';

interface Paper {
    abstract: string;
    authors: string;
    collaborators: string[];
    created: Date;
    date: string;
    id: string;
    link: string;
    tags: string[];
    title: string;
    updated: Date;
    venue: string;
}

interface Params {}
export const Collaboration = (_: RouteComponentProps<Params>) => {
    const { response, loading, error } = useFetch(
        `${ApiBase}/papers?limit=1000&sort=date:desc`, // limit to top 1000 most recent (this is working around lack of pagination)
        {
            cachePolicy: 'no-cache',
        } as any,
        []
    );

    return (
        <div>
            {loading ? 'Loading...' : null}
            {error ? `Error: ${error}}` : null}
            {!error && response.data ? <Viz papers={response.data.items} /> : null}
        </div>
    );
};

const Viz = ({ papers }: { papers: Paper[] }) => {
    const dates = [
        { label: 'Year to date', start: dayjs().startOf('year'), end: dayjs() },
        { label: 'Rolling 365 days', start: dayjs().subtract(1, 'year'), end: dayjs() },
        ...[...Array(dayjs().year() - 2013).keys()].map((y) => {
            return {
                label: `SAB for ${dayjs().startOf('year').year() - y}`,
                start: dayjs().subtract(y, 'year').startOf('year'),
                end: dayjs().subtract(y, 'year').startOf('year').set('month', 8),
            };
        }),
        ...[...Array(dayjs().year() - 2014).keys()].map((y) => {
            return {
                label: `${dayjs().startOf('year').year() - y - 1}`,
                start: dayjs()
                    .subtract(y + 1, 'year')
                    .startOf('year'),
                end: dayjs().subtract(y, 'year').startOf('year'),
            };
        }),
    ];

    const format = 'MMM D, YYYY';
    return (
        <div>
            {dates.map((d) => (
                <div key={d.label}>
                    <textStyles.Jumbo>{d.label}</textStyles.Jumbo>
                    <div>
                        {d.start.format(format)} - {d.end.format(format)}
                    </div>
                    <Graph
                        papers={papers.filter(
                            (p) => dayjs(p.date).isAfter(d.start) && dayjs(p.date).isBefore(d.end)
                        )}
                    />
                    <Divider />
                </div>
            ))}
        </div>
    );
};

const Graph = ({ papers }: { papers: Paper[] }) => {
    if (!papers.length) {
        return <Empty />;
    }

    // To include, need to update here AND uncomment the line in nodes.
    const include = [
        'AllenNLP',
        'Mosaic',
        'Aristo',
        'PRIOR',
        'Semantic Scholar',
        'AI2 Israel',
        'University of Washington',
    ];
    // TODO: the above keys are the only ones shown ... to add a new COLAB, add itskey above
    // var include = ['{NEW_COLAB}', 'AllenNLP', ...];

    // Used to force layout into a circle.
    function getPos(i: number, total: number, radius: number) {
        const v = ((Math.PI * 2) / total) * i;
        const x = Math.cos(v) * radius;
        const y = Math.sin(v) * radius;
        return ` pos="${x},${y}!" `;
    }

    function getNodePos(i: number) {
        return getPos(i, include.length, 7.8);
        // TODO: if we add more COLABs, then we will want to increase the circle radius
        // return getPos(i, include.length, 4); // see 4
    }

    // Icons around circle.
    const nodes = [
        ' node [style="filled" shape=circle fontcolor=white color="#E9EAED" fillcolor="invis" height=3 width=3]',
        ' nodesep=0.1',
        ' rankdir=TB',
        // TODO: below carefully sets the order of COLABs.  To change order, just change the number in the getNodePos({NEW_NUMBER})
        // the numbers should go from 0-total_colabs... but the order of the lines does not matter
        // If you add a COLAB, you will need to add a new line to the list below
        // we have 2 sections below, 1 orens list, 2 maniltonian cycle:
        // with the current list of papers and COLABs, there is a Hamiltonian cycle that looks Necessitate
        // when we add papers or COLABs, we will want to cahnge the order below to make sure it still looks nice
        ` "AllenNLP" [id="n1" label="" ${getNodePos(0)} image="${allenNlpSrc}"]`,
        ` "Mosaic" [id="n2" label="" ${getNodePos(3)} image="${mosaicSrc}"]`,
        ` "Aristo" [id="n3" label="" ${getNodePos(4)} image="${aristoSrc}"]`,
        ` "PRIOR" [id="n4" label="" ${getNodePos(2)} image="${priorSrc}"]`,
        ` "Semantic Scholar" [id="n5" label="" ${getNodePos(1)} image="${semanticScholarSrc}"]`,
        ` "AI2 Israel" [id="n6" label="" ${getNodePos(
            5
        )} image="${israelSrc}" height=3.2 width=3.2]`,
        ` "University of Washington" [id="n7" label="" ${getNodePos(
            6
        )} image="${universityOfWashingtonSrc}"]`,
    ];

    // Remove " from titles.
    function cleanTitle(title: string) {
        return title.replaceAll('"', "'");
    }

    // Convert to dot format.
    function getLinks(affiliations: string[], label: string): string[] {
        const input = affiliations.filter(function (item) {
            return include.indexOf(item) !== -1;
        });
        const ret = [];
        for (let i: number = 0; i < input.length; i++) {
            for (let j: number = i + 1; j < input.length; j++) {
                // could use paper title (label) here if you want
                ret.push(
                    `"${input[j]}" -- "${input[i]}" [ id="e${i}-${j}-${cleanTitle(
                        label
                    )}" arrowhead=none color="#AFB4BD"]`
                );
            }
        }
        return ret;
    }

    const links: string[] = ([] as string[]).concat(
        ...papers.map((p) => getLinks([...p.collaborators, ...p.tags], p.title))
    );

    // Build wrapper for dot format.
    const dot = `graph {
        ${nodes.join('')}
        ${links.join('')}
    }`;

    return (
        <ScaleGraph
            dot={dot}
            images={[
                { src: aristoSrc, w: '224px', h: '224px' },
                { src: priorSrc, w: '224px', h: '224px' },
                { src: universityOfWashingtonSrc, w: '224px', h: '182px' },
                { src: mosaicSrc, w: '224px', h: '224px' },
                { src: semanticScholarSrc, w: '224px', h: '170px' },
                { src: allenNlpSrc, w: '224px', h: '224px' },
                { src: israelSrc, w: '224px', h: '138px' },
            ]}
        />
    );
};

const ScaleGraph = styled(Graphviz as any)`
    svg {
        width: 100%;
    }
`;
